/**
 * AcceptedOfferCard (consumer) — the first-class buyer-facing accepted-offer
 * card (M8-A UX polish, 2026-07-21). Mirrors the pro
 * `frontend/pro/src/components/clients/AcceptedOfferCard.tsx`: a distinct
 * green "deal" surface (not a plain card) with section HEADERS (Commission,
 * Rebate, Cash credits, Services) and the itemized pieces listed under each
 * as concise label→value rows, vertically aligned and consistent.
 *
 * Used on the first-class buyer surfaces — the "Your Offers" connected card
 * and /team's relationship profile — where the frozen deal deserves the
 * full layout.
 * The thread keeps the lighter collapsible `PinnedOfferCard` summary. Reuses
 * the shared offer-card formatters (never reimplemented); shows offer ITEMS
 * only, never a connection payment figure.
 */
import { BadgeCheck } from "lucide-react";
import {
  formatDollars,
  formatPercent,
  itemLabel,
} from "@sill/shared/components/marketplace/offer-card";
import type { PinnedOffer, PinnedOfferItem } from "../../lib/threads-api";

interface OfferRow {
  label: string;
  value: string | null;
}

interface OfferSection {
  header: string;
  rows: OfferRow[];
}

function rows(
  items: PinnedOfferItem[] | undefined,
  kind: "percent" | "money" | "none",
): OfferRow[] {
  return (items ?? []).map((item) => ({
    label: itemLabel(item.type),
    value:
      item.value == null || kind === "none"
        ? null
        : kind === "money"
          ? formatDollars(item.value)
          : formatPercent(item.value),
  }));
}

function offerSections(offer: PinnedOffer): OfferSection[] {
  const sections: OfferSection[] = [];
  if (offer.commission_pct != null) {
    sections.push({
      header: "Commission",
      rows: [
        { label: "Agent commission", value: formatPercent(offer.commission_pct) },
      ],
    });
  }
  const rebate = rows(offer.rebate, "percent");
  if (rebate.length > 0) sections.push({ header: "Rebate", rows: rebate });
  const cash = rows(offer.cash, "money");
  if (cash.length > 0) sections.push({ header: "Cash credits", rows: cash });
  const perks = rows(offer.perks, "none");
  if (perks.length > 0) sections.push({ header: "Services", rows: perks });
  return sections;
}

function cashTotal(offer: PinnedOffer): number {
  return (offer.cash ?? []).reduce((sum, item) => sum + (item.value ?? 0), 0);
}

export function AcceptedOfferCard({
  offer,
  heading = "Accepted offer",
}: {
  offer: PinnedOffer;
  heading?: string;
}) {
  const sections = offerSections(offer);
  if (sections.length === 0) return null;
  const total = cashTotal(offer);

  return (
    <section
      className="rounded-2xl bg-primary-soft p-5"
      data-testid="accepted-offer-card"
      aria-labelledby="accepted-offer-title"
    >
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-popover text-primary ring-1 ring-primary/15">
          <BadgeCheck className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h3
              id="accepted-offer-title"
              className="text-sm font-semibold text-foreground"
            >
              {heading}
            </h3>
            {total > 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="accepted-offer-total">
                <span className="font-semibold text-foreground">
                  {formatDollars(total)}
                </span>{" "}
                in cash incentives
              </p>
            ) : null}
          </div>

          <div className="mt-4 grid gap-x-10 gap-y-5 sm:grid-cols-2">
            {sections.map((section) => (
              <div
                key={section.header}
                data-testid={`accepted-offer-section-${section.header
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary">
                  {section.header}
                </p>
                <dl className="mt-2 space-y-1.5">
                  {section.rows.map((row, i) => (
                    <div
                      key={i}
                      className="flex items-baseline justify-between gap-4 border-b border-primary/10 pb-1.5 last:border-b-0 last:pb-0"
                    >
                      <dt className="text-sm text-foreground">{row.label}</dt>
                      {row.value != null ? (
                        <dd className="text-sm font-semibold tabular-nums text-foreground">
                          {row.value}
                        </dd>
                      ) : (
                        <dd className="text-xs font-semibold uppercase tracking-wide text-primary">
                          Included
                        </dd>
                      )}
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
